<template>
  <Loading v-if="isLoading" :message="$t('maps.loading')" />

  <!--
    7.5 — A CARTA NÁUTICA (D21). Os sete mapas não são um catálogo: são uma progressão com
    geografia — sai do píer, passa pela água doce, chega ao mar aberto. Sete pontos numa ordem
    fixa não pedem um mundo inteiro; pedem uma ROTA numa página de papel: o barco marca onde você
    está, parada bloqueada fica sob bruma e o requisito mora no painel de detalhe.

    Vive num componente próprio porque a carta tem DOIS lugares (D21, 7.5c): a página /maps e a
    folha que abre de dentro de Pescar — trocar de ponto sem sair da pescaria.
  -->
  <div v-else class="carta__papel" :class="{ 'carta__papel--folha': folha }">
    <FishNodeMap
      v-model="paradaAtiva"
      :nos="nos"
      arranjo="linear"
      :progresso="progressoDaRota"
      :lado="420"
      :rotulo="$t('maps.title')"
    >
      <template #detalhe="{ no }">
        <div v-if="mapaDe(no)" class="carta__detalhe">
          <div class="carta__cabecalho">
            <img
              class="carta__retrato"
              :class="{ 'carta__retrato--bruma': !canInteractWithMap(mapaDe(no)) }"
              :src="getItemImage(mapaDe(no).name)"
              :alt="mapaDe(no).name"
            >
            <div class="crescer">
              <p class="t-subtitulo carta__nome">{{ $t(`maps.mapNames.${getMapKey(mapaDe(no).name)}`) }}</p>
              <p v-if="currentMap === mapaDe(no).name" class="t-legenda carta__aqui">{{ $t('maps.buttons.currentMap') }}</p>
            </div>
          </div>

          <ul class="carta__requisitos">
            <li :class="{ 'carta__requisito--ok': nivelDoBarco >= mapaDe(no).required.boatId }">
              <FishIcon nome="ship" tamanho="pequeno" />
              {{ $t('maps.boat') }} {{ mapaDe(no).required.boatId }}+
            </li>
            <li :class="{ 'carta__requisito--ok': level >= mapaDe(no).required.level }">
              <FishIcon nome="star" tamanho="pequeno" />
              {{ $t('maps.level') }} {{ mapaDe(no).required.level }}+
            </li>
            <li v-if="mapaDe(no).required.gold > 0 && !isMapPurchased(mapaDe(no))" :class="{ 'carta__requisito--ok': playerGold >= mapaDe(no).required.gold }">
              <FishIcon nome="coins" tamanho="pequeno" />
              {{ formatGold(mapaDe(no).required.gold) }}
            </li>
          </ul>

          <!--
            8.7 — A VOLTA DA ENCICLOPÉDIA (D14, D21). A entrada do peixe já levava à carta; esta é
            a metade que faltava: quem vive nesta parada, com silhueta para o que ainda não foi
            pescado. É o que transforma "abrir um mapa" numa decisão informada em vez de um preço.

            A lista aparece mesmo na parada sob bruma, e é o ponto: saber o que se ganha ao chegar
            lá é justamente o motivo de querer chegar.
          -->
          <div v-if="peixesDoMapa(no).length" class="carta__vivem">
            <p class="carta__vivem-rotulo">
              {{ $t('maps.wholivesHere') }}
              <span class="t-numero carta__vivem-conta">
                {{ peixesDoMapa(no).filter((p) => p.descoberto).length }} / {{ peixesDoMapa(no).length }}
              </span>
            </p>
            <ul class="carta__peixes">
              <li v-for="peixe in peixesDoMapa(no)" :key="peixe.id">
                <img
                  class="carta__peixe-arte"
                  :class="{ 'carta__peixe-arte--silhueta': !peixe.descoberto }"
                  :src="peixe.imagem"
                  :alt="peixe.nome"
                  :aria-hidden="peixe.descoberto ? null : 'true'"
                >
                <span class="carta__peixe-nome">{{ peixe.nome }}</span>
              </li>
            </ul>
          </div>

          <div class="acao-principal carta__acao">
            <FishButton
              variante="primario"
              bloco
              :desabilitado="!canInteractWithMap(mapaDe(no)) || currentMap === mapaDe(no).name"
              :motivo="motivoDe(mapaDe(no))"
              :carregando="viajando === no.id"
              som="clique"
              @click="handleMapAction(no.id)"
            >
              {{ getButtonText(mapaDe(no)) }}
            </FishButton>
          </div>
        </div>
      </template>
    </FishNodeMap>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Loading from '@/shared/common/Loading.vue';
import { getItemImage } from '@/shared/utils/itemImages';
import { getFishImage } from '@/shared/utils/fishesImages';
import { getMapKey } from '@/shared/utils/mapNames';
import * as v2 from '@/core/services/api/v2';
import { FishNodeMap, FishButton, FishIcon } from '@/shared/ui';

export default {
  name: 'CartaNautica',
  components: {
    Loading,
    FishNodeMap,
    FishButton,
    FishIcon
  },
  props: {
    /** Dentro de um modal (Pescar): sem moldura própria — o modal já é o papel. */
    folha: { type: Boolean, default: false },
  },
  emits: ['viajou'],
  data() {
    return {
      isLoading: true,
      paradaAtiva: null,
      viajando: null,
      /** A estante do D14, agrupada por mapa. Vazia enquanto não responde — a carta não espera. */
      peixesPorMapa: {},
    }
  },
  async created() {
    if (!this.maps || Object.keys(this.maps).length === 0) {
      await this.fetchMaps();
    }
    this.isLoading = false;
    // A carta abre na parada em que o barco está — é dali que se parte.
    const atual = this.mapasOrdenados.find((m) => m.name === this.currentMap);
    this.paradaAtiva = atual ? atual.id : this.mapasOrdenados[0]?.id ?? null;

    // Depois de desenhar, e sem `await` na frente da rota: a carta é útil sem a lista de peixes, e
    // esperar a segunda chamada só atrasaria o que já dá para mostrar.
    void this.carregarPeixes();
  },
  computed: {
    ...mapState('player', ['gold', 'equipment', 'purchasedMaps', 'level', 'currentMap']),
    ...mapState('maps', ['maps']),
    playerGold() {
      return this.gold || 0;
    },
    nivelDoBarco() {
      return this.equipment?.boat?.level || 0;
    },
    /** A rota tem ordem: do píer para longe da costa. O nível exigido é a régua natural. */
    mapasOrdenados() {
      return Object.values(this.maps || {})
        .filter(Boolean)
        .sort((a, b) => (a.required?.level || 0) - (b.required?.level || 0) || (a.required?.gold || 0) - (b.required?.gold || 0));
    },
    nos() {
      return this.mapasOrdenados.map((mapa, i) => ({
        id: mapa.id,
        grau: i + 1,
        rotulo: this.$t(`maps.mapNames.${this.getMapKey(mapa.name)}`),
        aqui: this.currentMap === mapa.name,
        bruma: !this.canInteractWithMap(mapa),
        aceso: this.canInteractWithMap(mapa) && this.currentMap !== mapa.name,
      }));
    },
    /** A trilha dourada vai até a parada mais distante que você já alcança. */
    progressoDaRota() {
      const total = this.mapasOrdenados.length;
      if (total < 2) return 0;
      let ultima = 0;
      this.mapasOrdenados.forEach((mapa, i) => {
        if (this.canInteractWithMap(mapa)) ultima = i;
      });
      return ultima / (total - 1);
    }
  },
  methods: {
    ...mapActions('maps', ['fetchMaps']),
    ...mapActions('player', ['changeCurrentMap', 'purchaseMap']),
    getItemImage(name) {
      return getItemImage(name);
    },

    /**
     * Quem vive em cada parada (8.7).
     *
     * Vem da enciclopédia e não de uma rota nova: aquela resposta já traz, para cada peixe, os
     * mapas em que ele aparece **e** se este jogador já o pescou — que é exatamente o par que a
     * parada precisa. Uma rota "peixes do mapa X" devolveria a primeira metade e obrigaria uma
     * segunda chamada para a outra.
     *
     * Falha em silêncio de propósito: sem a lista a carta continua inteira, e um erro vermelho por
     * cima da rota seria pior do que a ausência de um enfeite informativo.
     */
    async carregarPeixes() {
      const playerId = this.$store.state.player.id;
      if (!playerId) return;
      try {
        const estante = await v2.enciclopedia(playerId, this.$i18n.locale);
        const porMapa = {};
        for (const item of estante.itens || []) {
          for (const mapa of item.mapas || []) {
            (porMapa[mapa.id] ??= []).push({
              id: item.id,
              descoberto: item.descoberto,
              // `getFishImage`: o mapa de itens não tem peixe nenhum. E `arte` e não `nome`, porque
              // o não descoberto tem nome nulo — sem a chave, todos cairiam no mesmo desenho.
              imagem: item.imagem || getFishImage(item.arte),
              nome: item.descoberto
                ? this.nomeDoPeixe(item.nome)
                : this.$t('inventory.enciclopedia.porDescobrir'),
            });
          }
        }
        this.peixesPorMapa = porMapa;
      } catch (error) {
        console.error('Erro ao carregar os peixes da carta:', error);
      }
    },

    peixesDoMapa(no) {
      return this.peixesPorMapa[no?.id] || [];
    },

    /** O nome traduzido mora no i18n, como na estante do caderno — nunca no servidor. */
    nomeDoPeixe(nome) {
      const chave = `fishes.${nome}`;
      const traduzido = this.$t(chave);
      return traduzido === chave ? nome : traduzido;
    },
    mapaDe(no) {
      if (!no) return null;
      return this.mapasOrdenados.find((m) => m.id === no.id) || null;
    },
    formatGold(amount) {
      return new Intl.NumberFormat('pt-BR').format(amount);
    },
    /** Nível do jogador e do barco equipado. O ouro fica fora: é só para comprar. */
    meetsRequirements(map) {
      return this.nivelDoBarco >= map.required.boatId && this.level >= map.required.level;
    },
    /**
     * "Comprado" quer dizer "posso viajar": `purchasedMaps` é a lista de **ids** de
     * `GET /player/:id/maps`, e mapa gratuito (`gold` 0) nunca passa por compra.
     */
    isMapPurchased(map) {
      if (!map) return false;
      if ((map.required?.gold || 0) === 0) return this.meetsRequirements(map);
      return Array.isArray(this.purchasedMaps) && this.purchasedMaps.includes(map.id);
    },
    isMapUnlocked(map) {
      if (!map) return false;
      return this.meetsRequirements(map) && this.playerGold >= map.required.gold;
    },
    canInteractWithMap(map) {
      if (!map) return false;
      return this.isMapPurchased(map) || this.isMapUnlocked(map);
    },
    getButtonText(map) {
      if (!map) return this.$t('maps.buttons.unavailable');
      if (this.currentMap === map.name) return this.$t('maps.buttons.currentMap');
      if (this.isMapPurchased(map)) return this.$t('maps.buttons.select');
      if (this.isMapUnlocked(map)) return `${this.$t('maps.buttons.unlock')} · ${this.formatGold(map.required.gold)}`;
      return this.$t('maps.buttons.unavailable');
    },
    /** O que falta, escrito no botão (D11) — a carta não tem porta trancada muda. */
    motivoDe(map) {
      if (!map || this.canInteractWithMap(map)) return '';
      if (this.nivelDoBarco < map.required.boatId) return this.$t('maps.buttons.insufficientBoat');
      if (this.level < map.required.level) return this.$t('maps.buttons.insufficientLevel');
      return this.$t('maps.buttons.insufficientGold');
    },
    async handleMapAction(mapId) {
      const map = this.mapasOrdenados.find((m) => m.id === mapId);
      if (!map) return;

      this.viajando = mapId;
      try {
        if (this.isMapPurchased(map)) {
          // Quem cobra, confere requisitos e diz o ouro restante é o servidor (nada de
          // DEDUCT_GOLD local — a lição da migração V2).
          await this.changeCurrentMap(map.id);
          // Quem abriu a carta de dentro de Pescar quer voltar à pescaria no mapa novo.
          this.$emit('viajou', map);
        } else if (this.isMapUnlocked(map)) {
          await this.purchaseMap(map.id);
        }
      } catch (error) {
        console.error('Error on map action:', error);
      } finally {
        this.viajando = null;
      }
    },
    getMapKey
  }
};
</script>

<style scoped>
/* A PÁGINA: a rota mora num papel de carta, não solta na água (D21). É a moldura do pack com o
 * miolo de pergaminho — o mesmo material do caderno. */
.carta__papel {
  max-width: 760px;
  margin: 0 auto;
  border: var(--moldura-borda) solid transparent;
  border-image: url('/ui/frame-panel.png') var(--moldura-fatia) / var(--moldura-borda) repeat;
  background: var(--papel);
  background-clip: padding-box;
  image-rendering: pixelated;
  padding: var(--e-4);
  box-shadow: 0 6px 0 rgba(0, 0, 0, 0.35);
}

/* Dentro do modal de Pescar o papel já existe (o próprio modal): a moldura dupla sairia moldura
 * dentro de moldura. */
.carta__papel--folha {
  border: none;
  border-image: none;
  background: none;
  box-shadow: none;
  padding: 0;
}

/* A rota EM CIMA e o detalhe embaixo, como legenda, é o próprio arranjo linear do `FishNodeMap` —
 * não uma exceção da carta. Havia aqui um `:deep` que virava a direção do flex e só isso, deixando
 * de pé o `align-items: flex-start` do lado-a-lado; era ele que tirava o `stretch` do painel e
 * fazia o detalhe sair do papel com muitos peixes e encolher com poucos. */

.carta__detalhe {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-3);
}

.carta__cabecalho {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  margin-bottom: var(--e-3);
}

.carta__retrato {
  width: 96px;
  height: 72px;
  object-fit: cover;
  border: 3px solid var(--madeira);
  image-rendering: auto;
}

/* A parada sob bruma mostra a paisagem VELADA, não apagada: silhueta dá curiosidade (D21/D14). */
.carta__retrato--bruma {
  filter: grayscale(0.8) brightness(0.6);
}

.carta__nome {
  margin: 0;
  color: var(--tinta);
}

.carta__aqui {
  margin: 0;
  color: var(--latao);
  font-family: var(--fonte-display);
}

.carta__requisitos {
  list-style: none;
  margin: 0 0 var(--e-4);
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-2) var(--e-4);
}

.carta__requisitos li {
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  color: var(--tinta-fraca);
}

/* Requisito cumprido: tinta firme e sem drama. O que falta fica fraco — é o que se lê primeiro. */
.carta__requisito--ok {
  color: var(--tinta);
}

.carta__acao {
  background: none;
  padding: 0;
}

/* ------------------------------------------------ quem vive nesta parada (8.7) */

.carta__vivem {
  border-top: 2px dashed var(--papel-sombra);
  padding-top: var(--e-2);
  margin-bottom: var(--e-4);
}

.carta__vivem-rotulo {
  margin: 0 0 var(--e-2);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.carta__vivem-conta {
  float: right;
  color: var(--latao);
}

/* Uma tira que rola na horizontal, e não uma grade: a lista é anexo do detalhe da parada, e uma
 * grade cresceria para baixo empurrando a ação principal para fora do papel (UI-17). */
.carta__peixes {
  list-style: none;
  margin: 0;
  padding: 0 0 var(--e-1);
  display: flex;
  gap: var(--e-3);
  overflow-x: auto;
}

.carta__peixes li {
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Sem encolher: a tira rola, os desenhos não se espremem. */
  flex: 0 0 auto;
  width: 72px;
}

.carta__peixe-arte {
  width: 64px;
  height: 48px;
  object-fit: contain;
  image-rendering: auto;
}

/* A mesma silhueta do caderno (D14): `brightness(0)` preserva o alfa, então é a forma do peixe e
 * não um retângulo, e a opacidade a traz para o tom de lápis do papel. */
.carta__peixe-arte--silhueta {
  filter: brightness(0);
  opacity: 0.28;
}

.carta__peixe-nome {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  text-align: center;
  line-height: 1.2;
}
</style>
